import { Flex, Text } from "@fluentui/react-northstar";
import { totalQuizPoints, totalUserQuizPoints } from "@teams/client-shared";
import { TeamsSurveyResponse } from "@teams/types";
import { SurveyDTO } from "@teams/types/api";
import React, { useEffect, useMemo, useState } from "react";

import { TeamsLoader } from "../../../../_shared/components/TeamsLoader";
import { isError } from "../../../../_shared/services/ChannelApi";
import { surveyApi } from "../../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../../_shared/state/TeamsContextProvider";

type Props = {
  survey: SurveyDTO;
  showPostResultsAction?: boolean;
  maxDisplay?: number;
};

export const TriviaLeaderboard: React.FC<Props> = ({ survey, maxDisplay }) => {
  const [responses, setResponses] = useState<TeamsSurveyResponse[]>();
  const { teamsContext } = useTeamsContext();

  useEffect(() => {
    (async () => {
      const result = await surveyApi.surveyResponses(teamsContext, survey.id!);
      if (!isError(result)) {
        setResponses(result.responses);
      }
    })();
  }, [survey.id, teamsContext]);

  const totalPoints = useMemo(
    () => totalQuizPoints(survey),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [survey.id]
  );

  const placementResults = useMemo(
    () => {
      if (!responses) {
        return undefined;
      }

      const responseDisplays = responses.map(response => {
        return {
          responseId: response.id,
          user: response.user,
          points: totalUserQuizPoints(survey.questions, response),
        };
      });

      responseDisplays.sort((a, b) => b.points - a.points);

      const firstPlacePoints = responseDisplays.length > 0 ? responseDisplays[0].points : undefined;
      let secondPlacePoints: number | undefined;
      let thirdPlacePoints: number | undefined;

      if (firstPlacePoints) {
        const secondPlace = responseDisplays.find(r => r.points < firstPlacePoints);
        if (secondPlace) {
          secondPlacePoints = secondPlace.points;
          thirdPlacePoints = secondPlacePoints
            ? responseDisplays.find(r => r.points < secondPlace.points)?.points
            : undefined;
        }
      }

      if (maxDisplay) {
        responseDisplays.splice(maxDisplay);
      }

      return { responseDisplays, firstPlacePoints, secondPlacePoints, thirdPlacePoints };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [survey.id, responses, totalPoints]
  );

  if (!placementResults || !responses) {
    return <TeamsLoader isLoading loadingMessage="Loading results..." />;
  }

  return (
    <Flex gap="gap.medium" column>
      {survey.state === "closed" && (
        <>
          {!!placementResults.firstPlacePoints && (
            <Text
              align="center"
              weight="bold"
              content={`🎉 Well done ${placementResults.responseDisplays
                .filter(r => r.points === placementResults.firstPlacePoints)
                .map(r => r.user.name)
                .join(", ")} 🎉`}
            />
          )}
          {!placementResults.firstPlacePoints && (
            <Text content="😬 Nobody took part in this Trivia" weight="regular" align="center" />
          )}
        </>
      )}

      {placementResults.responseDisplays.length > 0 && (
        <table>
          <thead>
            <tr>
              <th style={{ width: "75px" }}>Place</th>
              <th>User</th>
              <th>Correct</th>
            </tr>
          </thead>

          <tbody>
            {placementResults.responseDisplays.map(row => (
              <tr key={row.responseId}>
                <td>{TriviaPlacementEmoji(row.points, placementResults)}</td>
                <td>
                  <Text content={row.user.name} weight="bold" />
                </td>
                <td>
                  {row.points}/{totalPoints} correct
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {maxDisplay && responses.length > maxDisplay && (
        <div className="quiz-leader-board__more-responses">
          <Text size="small" weight="bold" content={`+${responses.length - maxDisplay} more`} />
        </div>
      )}
    </Flex>
  );
};

const TriviaPlacementEmoji = (
  points: number,
  placements: {
    firstPlacePoints: number | undefined;
    secondPlacePoints: number | undefined;
    thirdPlacePoints: number | undefined;
  }
): string => {
  if (placements.firstPlacePoints && points === placements.firstPlacePoints) {
    return "🥇 1st";
  }

  if (placements.secondPlacePoints && points === placements.secondPlacePoints) {
    return "🥈 2nd";
  }

  if (placements.thirdPlacePoints && points === placements.thirdPlacePoints) {
    return "🥉 3rd";
  }

  return "🏆";
};
